import type { Rng } from '../../core/rng';
import type { AABB, OBB, Vec2 } from '../../geo/types';
import { createSpatialHash, type SpatialHash } from '../../geo/spatialHash';
import type { PolygonIndex } from '../../geo/polygonIndex';
import { obbOverlap } from '../../geo/sat';
import { obbCorners } from '../../geo/polygon';
import { polylineLength, pointAtDistance } from '../../geo/polyline';
import type { Craft } from './harbourZones';
import type { ShipType, SailState, BoatAppearance } from './shipTemplates';
import { shipPlan, planBox } from './shipFootprint';
import {
  CRAFTS_BY_SIZE,
  SHIP_PAINTER_SCALE_H,
  meetsTaken,
  painterPlan,
  shipDeal,
  shipOf,
  takePlan,
  takenPlans,
  typeOfCraft,
  type PlacedShip,
  type ShipDeal,
  type ShipPose,
} from './shipPlacement';

/** A bank with the boats the painting shows moored along it. */
export interface MooringData {
  readonly id: string;
  readonly path: readonly Vec2[];
  /** The side of the path, looking along it, where the water and the boats lie. */
  readonly side: 'left' | 'right';
  readonly craft: Readonly<Record<Craft, number>>;
  /** Of those, the boats with a sail spread. */
  readonly underSail: Readonly<Record<Craft, number>>;
  /** A painted group attached to a bridge edge, included in this bank's existing craft tally. */
  readonly bridgeBerths?: readonly BridgeBerth[];
}

interface BridgeBerth {
  readonly path: readonly Vec2[];
  readonly side: 'left' | 'right';
  readonly count: number;
}

/** The water boats moor on and the bridge decks over it, as boxes in plan. */
export interface Quayside {
  readonly waterIndex: PolygonIndex;
  readonly bridges: readonly OBB[];
}

const STERN_OFF_BANK_M = 1;
const MAX_YAW = (3 * Math.PI) / 180;
const FREE_YAW = (30 * Math.PI) / 180;
/**
 * PLAN Ships, moorings (2026-10-06): the painting draws the Damrak's boats in close fans, hull
 * beside hull and many at one skew, with open water between the fans. The boats of a group of
 * `group` lie `gap` m apart at their own size and share a skew up to `fan`, each within `jitter`
 * of it; in the painter's view their grown hulls overlap, as the painted ones do. Against the
 * vessels placed before a bank's boats, the water's edge and the bridges the grown hull still has
 * to fit. Which boats share a group is the model's.
 */
const BERTH = {
  gap: [0.25, 0.9],
  group: [2, 6],
  fan: (25 * Math.PI) / 180,
  jitter: (4 * Math.PI) / 180,
  clearance: 0.15,
  cell: 16,
} as const;
const PROBE_STEP_M = 1;
/* A boat whose slot a bend of the bank or a neighbour blocks slides along the bank, a metre at a
   time, up to the width of a boat's plan at painter scale. */
const SLIDE_STEP_M = 1;
const MAX_SLIDE_M = 12;

/** A stretch of the bank, as distances along it. */
type Stretch = readonly [number, number];

interface Boat {
  readonly type: ShipType;
  readonly sailState: SailState;
  readonly boat?: BoatAppearance;
}

/** The deck of a bridge as a box in plan. */
export function deckBox(bridge: {
  readonly a: Vec2;
  readonly b: Vec2;
  readonly width: number;
}): OBB {
  const [dx, dy] = [bridge.b[0] - bridge.a[0], bridge.b[1] - bridge.a[1]];
  const length = Math.hypot(dx, dy);
  return {
    center: [(bridge.a[0] + bridge.b[0]) / 2, (bridge.a[1] + bridge.b[1]) / 2],
    axisU: [dx / length, dy / length],
    halfU: length / 2,
    halfV: bridge.width / 2,
  };
}

/* The bridge crop shows long narrow lighters interspersed with smaller open boats. The scan
   supplies the differing silhouettes, not measured historical hull dimensions. */
function mooredType(craft: Craft, index: number, deal: ShipDeal): ShipType {
  return craft === 'unmasted' ? (index % 5 === 4 ? 'rowing' : 'praam') : typeOfCraft(craft, deal);
}

function crewOf(mooring: MooringData, rng: Rng, deal: ShipDeal): readonly Boat[] {
  const hulls = rng.fork('hulls');
  const finishes = ['tarred', 'weathered', 'oak'] as const;
  const boats = CRAFTS_BY_SIZE.flatMap((craft) =>
    Array.from({ length: mooring.craft[craft] }, (_, i): Boat => {
      const type = mooredType(craft, i, deal);
      const boat =
        craft === 'unmasted'
          ? {
              lengthScale: hulls.range(
                type === 'praam' ? 0.85 : 0.9,
                type === 'praam' ? 1.55 : 1.25
              ),
              beamScale: hulls.range(0.82, 1.16),
              finish: finishes[i % finishes.length]!,
            }
          : undefined;
      return {
        type,
        sailState: i < mooring.underSail[craft] ? 'set' : 'furled',
        ...(boat ? { boat } : {}),
      };
    })
  );
  return rng.shuffle(boats);
}

/* The stretch of bank a boat takes at its own size, turned the largest yaw towards a neighbour
   turned the other way: its breadth, and its far end leaning along the bank. */
function slotWidth(boat: Pick<Boat, 'type' | 'boat'>): number {
  const plan = shipPlan(boat.type, boat.boat);
  const reach = STERN_OFF_BANK_M + plan.fore + plan.aft;
  return 2 * (plan.halfWidth * Math.cos(MAX_YAW) + reach * Math.sin(MAX_YAW));
}

interface Berth {
  /** The distance along the free bank of the berth's middle. */
  readonly at: number;
  /** The skew the boat lies at, its group's and its own. */
  readonly yaw: number;
}

/*
 * The berths along a free bank, for as many boats as it holds: groups of boats a hull's breadth
 * apart at one skew, and the bank's remaining length shared out between the groups and its ends.
 */
function berthsAlong(boats: readonly Boat[], length: number, rng: Rng): readonly Berth[] {
  const widths = boats.map(slotWidth);
  const ends = widths.reduce<number[]>((sums, w) => [...sums, (sums.at(-1) ?? 0) + w], []);
  const held = ends.filter((end) => end <= length).length;
  if (held === 0) return [];
  const sizes = rng.fork('groups');
  const firsts = new Set<number>();
  for (let i = sizes.int(BERTH.group[0], BERTH.group[1]); i < held;)
    i += (firsts.add(i), sizes.int(BERTH.group[0], BERTH.group[1]));
  const gaps = rng.fork('gaps');
  const inGroup = Array.from({ length: held }, (_, i) =>
    i === 0 || firsts.has(i) ? 0 : gaps.range(BERTH.gap[0], BERTH.gap[1])
  );
  const packed = ends[held - 1]! + inGroup.reduce((sum, gap) => sum + gap, 0);
  const tight = packed > length ? (length - ends[held - 1]!) / (packed - ends[held - 1]!) : 1;
  const slack = Math.max(0, length - ends[held - 1]! - (packed - ends[held - 1]!) * tight);
  const shares = rng.fork('shares');
  const weights = Array.from({ length: firsts.size + 2 }, (_, i) =>
    i === 0 || i === firsts.size + 1 ? 0.4 : shares.range(1, 3)
  );
  const total = weights.reduce((sum, w) => sum + w, 0);
  const fans = rng.fork('fans');
  let at = (slack * weights[0]!) / total;
  let group = 0;
  let fan = fans.range(-BERTH.fan, BERTH.fan);
  return widths.slice(0, held).map((w, i) => {
    if (firsts.has(i)) {
      group++;
      at += (slack * weights[group]!) / total;
      fan = fans.range(-BERTH.fan, BERTH.fan);
    }
    at += inGroup[i]! * tight;
    const berth = { at: at + w / 2, yaw: fan + fans.range(-BERTH.jitter, BERTH.jitter) };
    at += w;
    return berth;
  });
}

/* A boat's hull in plan at its own size, with the room it keeps off the next. */
function hullPlan(pose: ShipPose): OBB {
  const hull = planBox(shipPlan(pose.type, pose.boat), pose.position, pose.heading);
  return { ...hull, halfU: hull.halfU + BERTH.clearance, halfV: hull.halfV + BERTH.clearance };
}

function boundsOf(box: OBB): AABB {
  const corners = obbCorners(box);
  const xs = corners.map((c) => c[0]);
  const ys = corners.map((c) => c[1]);
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
  };
}

/** The vessels a boat has to keep clear of: those placed before its bank's, and its bank's own. */
interface Moored {
  /** The plans at the painter's scale of the vessels placed before this bank's boats. */
  readonly taken: SpatialHash<OBB>;
  /** The hulls at their own size of this bank's boats. */
  readonly berthed: SpatialHash<OBB>;
}

function berth(moored: Moored, pose: ShipPose): void {
  const hull = hullPlan(pose);
  moored.berthed.insert(hull, boundsOf(hull));
}

function poseAt(
  mooring: MooringData,
  along: number,
  boat: Pick<Boat, 'type' | 'boat'>,
  yaw: number
): ShipPose {
  const { point, tangent } = pointAtDistance(mooring.path, along);
  const sign = mooring.side === 'left' ? 1 : -1;
  const out = Math.atan2(sign * tangent[0], -sign * tangent[1]) + yaw;
  const reach = shipPlan(boat.type, boat.boat).aft + STERN_OFF_BANK_M;
  return {
    type: boat.type,
    ...(boat.boat ? { boat: boat.boat } : {}),
    position: [point[0] + reach * Math.cos(out), point[1] + reach * Math.sin(out)],
    heading: out + Math.PI,
  };
}

function underBridge(quayside: Quayside, pose: ShipPose): boolean {
  const painted = painterPlan(pose, 'stern');
  return quayside.bridges.some((deck) => obbOverlap(painted, deck, 0));
}

/* The stretches of the bank where the widest boat, moored square to it, meets no bridge. */
function freeStretches(
  mooring: MooringData,
  quayside: Quayside,
  widest: Pick<Boat, 'type' | 'boat'>
): readonly Stretch[] {
  const count = Math.floor(polylineLength(mooring.path) / PROBE_STEP_M) + 1;
  const clear = Array.from(
    { length: count },
    (_, i) => !underBridge(quayside, poseAt(mooring, i * PROBE_STEP_M, widest, 0))
  );
  return clear.reduce<Stretch[]>((runs, isClear, i) => {
    if (!isClear) return runs;
    const at = i * PROBE_STEP_M;
    const last = runs.at(-1);
    return last && clear[i - 1] ? [...runs.slice(0, -1), [last[0], at]] : [...runs, [at, at]];
  }, []);
}

/* The distance along the bank that lies `free` metres into its free stretches. */
function alongBank(stretches: readonly Stretch[], free: number): number {
  let rest = free;
  for (const [from, to] of stretches) {
    if (rest <= to - from) return from + rest;
    rest -= to - from;
  }
  return stretches.at(-1)?.[1] ?? 0;
}

function blocked(quayside: Quayside, moored: Moored, pose: ShipPose): boolean {
  if (meetsTaken(moored.taken, painterPlan(pose, 'stern')) || underBridge(quayside, pose))
    return true;
  const hull = hullPlan(pose);
  if (moored.berthed.query(boundsOf(hull)).some((other) => obbOverlap(hull, other, 0))) return true;
  return (
    !quayside.waterIndex.obbInside(
      planBox(shipPlan(pose.type, pose.boat), pose.position, pose.heading)
    ) || !quayside.waterIndex.obbInside(painterPlan(pose, 'stern'))
  );
}

function slid(
  mooring: MooringData,
  quayside: Quayside,
  moored: Moored,
  slot: { readonly along: number; readonly craft: Boat; readonly yaw: number },
  maxSlide = MAX_SLIDE_M,
  slideStep = SLIDE_STEP_M
): ShipPose | null {
  const length = polylineLength(mooring.path);
  for (let step = 0; step <= maxSlide / slideStep; step++) {
    for (const sign of step === 0 ? [1] : [1, -1]) {
      const along = slot.along + sign * step * slideStep;
      if (along < 0 || along > length) continue;
      const pose = poseAt(mooring, along, slot.craft, slot.yaw);
      if (!blocked(quayside, moored, pose)) return pose;
    }
  }
  return null;
}

/* The furthest a boat's plan at painter scale reaches from its point on the bank. */
function reachOf(boat: Pick<Boat, 'type' | 'boat'>): number {
  const plan = shipPlan(boat.type, boat.boat);
  return STERN_OFF_BANK_M + SHIP_PAINTER_SCALE_H * (plan.fore + plan.aft + plan.halfWidth);
}

/* Only the bridges within a boat's reach of the bank can meet its plan; the rest need no test. */
function nearBank(quayside: Quayside, path: readonly Vec2[], reach: number): Quayside {
  const xs = path.map((p) => p[0]);
  const ys = path.map((p) => p[1]);
  const [minX, maxX] = [Math.min(...xs) - reach, Math.max(...xs) + reach];
  const [minY, maxY] = [Math.min(...ys) - reach, Math.max(...ys) + reach];
  const bridges = quayside.bridges.filter((deck) => {
    const corners = obbCorners(deck);
    return (
      corners.some((c) => c[0] >= minX) &&
      corners.some((c) => c[0] <= maxX) &&
      corners.some((c) => c[1] >= minY) &&
      corners.some((c) => c[1] <= maxY)
    );
  });
  return { ...quayside, bridges };
}

/* Bridge-side lighters are selected from this bank's recorded crew, not added to it. If a berth
   cannot hold one at both scales, that craft remains in the ordinary bank allocation. */
function alongsideBridge(
  mooring: MooringData,
  quayside: Quayside,
  boats: readonly Boat[],
  moored: Moored,
  rng: Rng,
  warn: (message: string) => void
): ReadonlyMap<Boat, ShipPose> {
  const placed = new Map<Boat, ShipPose>();
  for (const [index, berth] of (mooring.bridgeBerths ?? []).entries()) {
    const before = placed.size;
    const craft = boats
      .filter((boat) => boat.type === 'praam' && !placed.has(boat))
      .slice(0, berth.count);
    const edge: MooringData = { ...mooring, path: berth.path, side: berth.side };
    /* The four painted lighters lie apart along the bridge, each clear of the next in the
       painter's view too, so they are spread over its edge and not berthed as a group. */
    const span = polylineLength(berth.path) / Math.max(1, craft.length);
    const gaps = rng.fork(`bridge-gaps-${index}`);
    const yaws = rng.fork(`bridge-yaws-${index}`);
    for (const [i, boat] of craft.entries()) {
      const along = span * (i + 0.5 + gaps.range(-0.12, 0.12));
      const yaw = yaws.range(-FREE_YAW / 2, FREE_YAW / 2);
      const pose = [yaw, 0].reduce<ShipPose | null>(
        (found, turn) =>
          found ?? slid(edge, quayside, moored, { along, craft: boat, yaw: turn }, 3),
        null
      );
      if (!pose) continue;
      placed.set(boat, pose);
      takePlan(moored.taken, pose, 'stern');
    }
    if (placed.size - before < berth.count) {
      warn(`Mooring ${mooring.id}: bridge berths ${placed.size - before}/${berth.count}`);
    }
  }
  return placed;
}

/**
 * Moors the painted boats of a bank stern-to along it, in the craft and with the sails painted, its
 * ships of the types dealt (its own deal unless the fleet's is given), in a shuffled order: clear
 * of the bridges, on the water and clear of every vessel placed before, at painter scale. The
 * boats the bank has no room for are left out and reported.
 */
export function placeMooredBoats(
  mooring: MooringData,
  quayside: Quayside,
  existing: readonly PlacedShip[],
  rng: Rng,
  warn: (message: string) => void,
  deal: ShipDeal = shipDeal(rng.fork('types'))
): readonly PlacedShip[] {
  const boats = crewOf(mooring, rng.fork('crew'), deal);
  const bank = nearBank(quayside, mooring.path, Math.max(0, ...boats.map(reachOf)));
  const room: Moored = {
    taken: takenPlans(existing),
    berthed: createSpatialHash<OBB>(BERTH.cell),
  };
  const bridgeBoats = alongsideBridge(mooring, bank, boats, room, rng.fork('bridge-berths'), warn);
  const bankBoats = boats.filter((boat) => !bridgeBoats.has(boat));
  const widest = bankBoats.reduce<Pick<Boat, 'type' | 'boat'>>(
    (w, boat) => (slotWidth(boat) > slotWidth(w) ? boat : w),
    { type: 'rowing' }
  );
  const stretches = freeStretches(mooring, bank, widest);
  const free = stretches.reduce((sum, [from, to]) => sum + to - from, 0);
  const slots = berthsAlong(bankBoats, free, rng.fork('berth-gaps')).map((slot) => ({
    along: alongBank(stretches, slot.at),
    yaw: slot.yaw,
  }));
  const displaced: { readonly along: number; readonly craft: Boat; readonly index: number }[] = [];
  const moored = [...bridgeBoats].map(([boat, pose]) => ({
    ...shipOf(`${mooring.id}-bridge-boat-${boats.indexOf(boat)}`, pose, 0, 'stern'),
    sailState: boat.sailState,
  }));
  moored.push(
    ...slots
      .map(({ along, yaw }, slot) => ({ along, yaw, slot }))
      .flatMap(({ along, yaw, slot }) => {
        const boat = bankBoats[slot]!;
        const i = boats.indexOf(boat);
        // Keep the painted fleet count on crowded stretches; a group's skew uses its room first.
        const pose = [yaw, Math.max(-MAX_YAW, Math.min(MAX_YAW, yaw)), 0].reduce<ShipPose | null>(
          (found, turn) => found ?? slid(mooring, bank, room, { along, craft: boat, yaw: turn }),
          null
        );
        if (!pose) {
          displaced.push({ along, craft: boat, index: i });
          return [];
        }
        berth(room, pose);
        return [
          { ...shipOf(`${mooring.id}-boat-${i}`, pose, 0, 'stern'), sailState: boat.sailState },
        ];
      })
  );
  // Conservative yaw-sized slots may fill before the bank itself is full. Give the
  // remaining recorded craft the same collision-checked search as displaced boats.
  bankBoats.slice(slots.length).forEach((craft, i) => {
    displaced.push({
      along: (polylineLength(mooring.path) * (slots.length + i + 0.5)) / bankBoats.length,
      craft,
      index: boats.indexOf(craft),
    });
  });
  // A bend can block a nominal slot even when a neighbouring group has spare room.
  // Once the fleet is laid out, rehome those boats in the nearest clear gap along
  // the same bank, checking the full painted hull against water, decks and vessels.
  for (const slot of displaced) {
    const pose = slid(mooring, bank, room, { ...slot, yaw: 0 }, polylineLength(mooring.path), 0.25);
    if (!pose) continue;
    berth(room, pose);
    moored.push({
      ...shipOf(`${mooring.id}-boat-${slot.index}`, pose, 0, 'stern'),
      sailState: slot.craft.sailState,
    });
  }
  moored.sort((a, b) => Number(a.id.split('-').at(-1)) - Number(b.id.split('-').at(-1)));
  if (moored.length < boats.length) {
    warn(`Mooring ${mooring.id}: moored ${moored.length}/${boats.length} boats`);
  }
  return moored;
}
